import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { getBranchManager } from '../../../api/staff';
import { getSalesFilterProducts } from '../../../api/payment';
import {
  FILTER_FIELDS,
  FILTER_GROUPS,
  PRODUCT_SECTIONS,
  SEARCH_FIELD_KEY,
  isFieldVisible,
  isProductKindAllowed,
  buildFilterTokens,
  clearFilterField,
  countActiveFields,
  summarizeValues,
  toggleFilterValue,
} from './salesFilterSchema';
import {
  FilterPanelCard,
  FilterTabBar,
  SegmentTrack,
  FilterTab,
  FilterTabLabel,
  FilterTabBadge,
  FilterFieldList,
  FilterFieldRow,
  FilterFieldLabel,
  FilterFieldControl,
  ChipGroup,
  ChipToggleButton,
  FieldNote,
  FilterChip,
  FilterSearchRow,
  FilterSearchBox,
  FilterSearchInput,
  FilterSearchClear,
  ListToggleRow,
  ListToggleText,
  ListToggleSwitch,
  EndedTag,
  ListItemSub,
  ListInitialHeader,
  ProductPickRow,
  ProductCategorySelect,
  ListGroupHeader,
  ListSelectAnchor,
  ListSelectBox,
  ListSelectLabel,
  ListSelectCaret,
  ListPopover,
  ListPopHeader,
  ListPopCount,
  ListPopActions,
  ListPopAction,
  ListPopDivider,
  ListSearchInput,
  ListItems,
  ListItemRow,
  ListItemMark,
  ListItemLabel,
  ListDoneBtn,
  SelectedChipRow,
  SelectedChip,
  SelectedChipRemove,
  FilterFooter,
  FilterSummaryRow,
  FilterStatusPill,
  FilterTokenChip,
  FilterTokenField,
  FilterTokenValue,
  FilterTokenRemove,
  FilterMoreBtn,
  FilterActions,
  SearchButton,
  ResetButton,
} from './SalesManagTab1Styled';

// 요약 줄에 기본으로 펼치는 조건칩 수. 넘치면 [+N 더보기]로 전부 펼친다 — 감추지 않는다.
const TOKEN_PREVIEW_COUNT = 2;

// 목록 팝오버의 묶음 헤더 아이콘. 값마다 아이콘을 달지 않는 이유는 salesFilterSchema 주석 참조.
const GROUP_ICONS = {
  online: LanguageOutlinedIcon,
  message: SmsOutlinedIcon,
  offline: StorefrontOutlinedIcon,
  referral: PeopleOutlineOutlinedIcon,
  other: MoreHorizOutlinedIcon,
};

// 셀렉트 박스 안 필드 아이콘. 두 목록 필터가 같은 생김새라 아이콘이 구분을 맡는다.
const FIELD_ICONS = {
  visitPath: RouteOutlinedIcon,
  saleManager: PeopleOutlineOutlinedIcon,
  product: Inventory2OutlinedIcon,
};

const LIST_UNITS = { saleManager: '명' };

const PRODUCT_FIELD = FILTER_FIELDS.find(field => field.key === 'product');


// 판매종료 = 판매상태가 승인(APPROVED)이 아닌 수강권·이용권·일일권. 일반상품은 판매상태가 없어(null) 항상 판매중으로 본다.
const isEndedProduct = item => item.saleStatus != null && item.saleStatus !== 'APPROVED';

// 상품 목록 가나다 정렬 + 초성(ㄱㄴㄷ) 구분. 이름 앞 괄호·기호(<마스터>, [J] 등)는 건너뛰고 첫 글자로 본다.
// 쌍자음은 홑자음 묶음에 넣고(ㄲ→ㄱ), 한글로 시작하지 않는 이름은 맨 뒤 '#' 묶음.
const CHOSEONG = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const DOUBLE_TO_SINGLE = { ㄲ: 'ㄱ', ㄸ: 'ㄷ', ㅃ: 'ㅂ', ㅆ: 'ㅅ', ㅉ: 'ㅈ' };
const HANGUL_START = 0xac00;
const HANGUL_END = 0xd7a3;
const SYLLABLES_PER_INITIAL = 588;
const NON_HANGUL_INITIAL = '#';

const nameSortKey = label => (label || '').replace(/^[^0-9A-Za-z가-힣]+/, '');
const initialOf = label => {
  const code = nameSortKey(label).charCodeAt(0);
  if (code >= HANGUL_START && code <= HANGUL_END) {
    const initial = CHOSEONG[Math.floor((code - HANGUL_START) / SYLLABLES_PER_INITIAL)];
    return DOUBLE_TO_SINGLE[initial] || initial;
  }
  return NON_HANGUL_INITIAL;
};
const compareProductName = (a, b) =>
  (a.initial === NON_HANGUL_INITIAL) - (b.initial === NON_HANGUL_INITIAL) ||
  nameSortKey(a.label).localeCompare(nameSortKey(b.label), 'ko') ||
  (a.branchName || '').localeCompare(b.branchName || '', 'ko');

const SalesManagFilterPanel = ({
  filters,
  appliedFilters,
  onFiltersChange,
  onSearch,
  onReset,
  onRemoveToken,
  loading,
}) => {
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;

  const [activeGroup, setActiveGroup] = useState(FILTER_GROUPS[0].key);
  const [openList, setOpenList] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [tokensOpen, setTokensOpen] = useState(false);
  const [hideEndedProducts, setHideEndedProducts] = useState(true);
  // 상품 드롭다운 왼쪽 종류 선택 — 목록만 좁히는 화면 보조값이라 조회 조건에는 안 들어간다. '' = 전체.
  const [productCategory, setProductCategory] = useState('');
  // 펼쳐 둔 접이식 칩 필드(상품 구분). 기본은 한 줄만.
  const [expandedChipFields, setExpandedChipFields] = useState([]);
  const listAnchorRef = useRef(null);

  // 판매 담당자는 지점마다 달라지므로 런타임에 채운다.
  // managerId 는 member_order.REGISTERED_BY 와 같은 user ID 공간이다(판매등록이 그 값을 그대로 저장).
  const { data: saleManagerOptions = [] } = useQuery({
    queryKey: ['salesFilterBranchManager', branchId],
    queryFn: async () => {
      const data = await getBranchManager(branchId, user.token);
      return (data || []).map(item => ({ value: String(item.managerId), label: item.managerName }));
    },
    enabled: !!branchId && !!user?.token,
  });

  // 상품 선택 — 조회 범위(본사 + 지점)의 주문에 실제로 등장한 상품. 값은 "KIND:id".
  const headBranchId = user?.headBranch?.headBranchId;
  const { data: productOptions = [] } = useQuery({
    queryKey: ['salesFilterProducts', headBranchId, branchId],
    queryFn: async () => {
      const params = new URLSearchParams({ headBranchId: String(headBranchId) });
      if (branchId) {
        params.set('branchId', String(branchId));
      }
      const data = await getSalesFilterProducts(params.toString(), user.token);
      return (data || []).map(item => ({
        value: `${item.kind}:${item.id}`,
        label: item.name,
        kind: item.kind,
        ended: isEndedProduct(item),
        branchName: item.branchName,
        initial: initialOf(item.name),
      }));
    },
    enabled: !!headBranchId && !!user?.token,
  });

  // 소유 지점이 둘 이상 섞일 때만 상품명 옆에 지점명을 단다(한 지점뿐이면 소음).
  const showProductBranch = useMemo(
    () => new Set(productOptions.map(option => option.branchName)).size > 1,
    [productOptions],
  );

  const optionOverrides = useMemo(
    () => ({ saleManager: saleManagerOptions, product: productOptions }),
    [saleManagerOptions, productOptions],
  );

  useEffect(() => {
    if (!openList) {
      return undefined;
    }
    const handleOutside = event => {
      if (listAnchorRef.current && !listAnchorRef.current.contains(event.target)) {
        setOpenList(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openList]);

  const optionsOf = field => optionOverrides[field.key] || field.options;

  const tokens = buildFilterTokens(appliedFilters, optionOverrides);
  const shownTokens = tokensOpen ? tokens : tokens.slice(0, TOKEN_PREVIEW_COUNT);
  const moreCount = tokens.length - shownTokens.length;
  // 적용된 조건이 없으면 요약 줄은 통째로 비운다 — 빈 상태 라벨은 자리만 먹는다.
  const hasSummary = tokens.length > 0;

  const currentFields = FILTER_FIELDS.filter(field => field.group === activeGroup && isFieldVisible(field, filters));
  const searchText = (filters[SEARCH_FIELD_KEY] || [])[0] || '';

  // 검색어는 [검색어] 한 칸 배열로 둔다(빈 값 = 조건 없음). Enter 는 하단 확정 버튼과 같다.
  const handleSearchText = text =>
    onFiltersChange({ ...filters, [SEARCH_FIELD_KEY]: text.trim() ? [text] : [] });

  const handleToggle = (field, value) => {
    onFiltersChange(toggleFilterValue(filters, field, value));
  };

  const renderChip = (field, option) => (
    <FilterChip
      key={option.value}
      selected={(filters[field.key] || []).includes(option.value)}
      onClick={() => handleToggle(field, option.value)}>
      {option.label}
    </FilterChip>
  );

  const renderChips = field => {
    const selected = filters[field.key] || [];
    const expanded = expandedChipFields.includes(field.key);
    const collapsible = field.collapsedCount != null && field.options.length > field.collapsedCount;
    const visibleOptions =
      collapsible && !expanded
        ? field.options.filter((option, index) => index < field.collapsedCount || selected.includes(option.value))
        : field.options;
    return (
      <ChipGroup>
        <FilterChip selected={selected.length === 0} onClick={() => onFiltersChange(clearFilterField(filters, field.key))}>
          전체
        </FilterChip>
        {visibleOptions.map(option => renderChip(field, option))}
        {collapsible && (
          <ChipToggleButton
            type="button"
            aria-label={expanded ? `${field.label} 접기` : `${field.label} 더보기`}
            aria-expanded={expanded}
            onClick={() =>
              setExpandedChipFields(prev =>
                expanded ? prev.filter(key => key !== field.key) : [...prev, field.key],
              )
            }>
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </ChipToggleButton>
        )}
      </ChipGroup>
    );
  };

  // 상품 드롭다운 묶음 — 상품구분을 골랐으면 그 구분만, 판매종료 숨기기면 판매중만.
  // 상품 구분(일일권은 일일권 구분까지 풀어서)·일일권 구분으로 허용되는 서버 kind.
  const isKindAllowed = kind => isProductKindAllowed(kind, filters);

  const allowedProductSections = () =>
    PRODUCT_SECTIONS.filter(section => section.kinds.some(isKindAllowed));
  // 종류 선택값이 상품 구분 변경으로 허용 범위를 벗어나면 '전체'로 본다(표시값과 목록 기준을 같게).
  const effectiveProductCategory = () =>
    allowedProductSections().some(section => section.value === productCategory) ? productCategory : '';

  const productSections = matches =>
    allowedProductSections()
      .filter(section => !effectiveProductCategory() || section.value === effectiveProductCategory())
      .map(section => ({
        key: section.value,
        label: section.label,
        items: matches
          .filter(
            option =>
              section.kinds.includes(option.kind) && isKindAllowed(option.kind) && !(hideEndedProducts && option.ended),
          )
          .sort(compareProductName),
      }))
      .filter(section => section.items.length > 0);

  // 팝오버 본체 — 퀵칩의 [더보기]와 셀렉트 박스가 같은 걸 연다.
  const renderListPopover = (field, options) => {
    const selected = filters[field.key] || [];
    const keyword = listSearch.trim();
    const matches = keyword ? options.filter(option => option.label.includes(keyword)) : options;
    // 묶음 정의가 있으면 묶음별로 끊어 보여주고, 없으면(판매 담당자) 한 덩어리로 둔다.
    const isProduct = field.key === 'product';
    const sections = isProduct
      ? productSections(matches)
      : field.optionGroups
      ? field.optionGroups
          .map(group => ({
            ...group,
            items: matches.filter(option => group.values.includes(option.value)),
          }))
          .filter(section => section.items.length > 0)
      : [{ key: 'all', label: null, items: matches }];

    const renderItem = option => {
      const on = selected.includes(option.value);
      return (
        <ListItemRow key={option.value} selected={on} onClick={() => handleToggle(field, option.value)}>
          <ListItemMark selected={on} multi={field.multi}>
            {on ? (field.multi ? '✓' : '●') : ''}
          </ListItemMark>
          <ListItemLabel>{option.label}</ListItemLabel>
          {isProduct && showProductBranch && option.branchName && <ListItemSub>{option.branchName}</ListItemSub>}
          {option.ended && <EndedTag>판매종료</EndedTag>}
        </ListItemRow>
      );
    };

    const visibleOptions = sections.flatMap(section => section.items);
    const selectableOptions = isProduct ? visibleOptions : options;

    return (
      <ListPopover wide={isProduct}>
        <ListPopHeader>
          <ListPopCount>
            {isProduct ? selectableOptions.filter(option => selected.includes(option.value)).length : selected.length} /{' '}
            {selectableOptions.length} 선택
          </ListPopCount>
          {field.multi && (
            <ListPopActions>
              <ListPopAction
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    [field.key]: Array.from(new Set([...selected, ...selectableOptions.map(option => option.value)])),
                  })
                }>
                전체선택
              </ListPopAction>
              <ListPopDivider />
              <ListPopAction muted onClick={() => onFiltersChange(clearFilterField(filters, field.key))}>
                해제
              </ListPopAction>
            </ListPopActions>
          )}
        </ListPopHeader>

        <ListSearchInput
          placeholder={isProduct ? '상품명 검색' : '검색'}
          value={listSearch}
          onChange={event => setListSearch(event.target.value)}
        />

        {isProduct && (
          <ListToggleRow>
            <ListToggleText>
              판매종료 숨기기
              <small>일반상품은 판매상태가 없어 항상 보입니다.</small>
            </ListToggleText>
            <ListToggleSwitch
              role="switch"
              aria-checked={hideEndedProducts}
              checked={hideEndedProducts}
              onClick={() => setHideEndedProducts(prev => !prev)}
            />
          </ListToggleRow>
        )}

        <ListItems>
          {isProduct && sections.length === 0 && <FieldNote>조건에 맞는 상품이 없습니다.</FieldNote>}
          {sections.map(section => {
            const GroupIcon = GROUP_ICONS[section.key];
            return (
              <React.Fragment key={section.key}>
                {section.label && (
                  <ListGroupHeader>
                    {GroupIcon && <GroupIcon />}
                    <span>{section.label}</span>
                  </ListGroupHeader>
                )}
                {isProduct
                  ? section.items.map((option, index) => (
                      <React.Fragment key={option.value}>
                        {(index === 0 || section.items[index - 1].initial !== option.initial) && (
                          <ListInitialHeader>{option.initial}</ListInitialHeader>
                        )}
                        {renderItem(option)}
                      </React.Fragment>
                    ))
                  : section.items.map(renderItem)}
              </React.Fragment>
            );
          })}
        </ListItems>

        <ListDoneBtn onClick={() => setOpenList(null)}>완료</ListDoneBtn>
      </ListPopover>
    );
  };

  // 목록형 필터(방문경로 · 판매 담당자)는 같은 드롭다운 생김새를 쓴다.
  const renderList = field => {
    const selected = filters[field.key] || [];
    const options = optionsOf(field);
    const isOpen = openList === field.key;
    const FieldIcon = FIELD_ICONS[field.key];
    const unit = LIST_UNITS[field.key] || '개';
    // 상품은 아래 칩 대신 박스 안에 이름을 보인다 — 하나면 그 이름, 여러 개면 "첫 상품 외 N개".
    const isProductField = field.key === 'product';
    const firstLabel = options.find(option => option.value === selected[0])?.label ?? selected[0];
    const boxLabel = isProductField && selected.length > 0
      ? selected.length === 1
        ? firstLabel
        : `${firstLabel} 외 ${selected.length - 1}개`
      : isProductField
      ? '상품 선택'
      : field.multi && selected.length > 0
      ? `${selected.length}${unit} 선택`
      : summarizeValues(field, selected, options);

    return (
      <>
        <ListSelectAnchor ref={isOpen ? listAnchorRef : null}>
          <ListSelectBox
            active={selected.length > 0}
            onClick={() => {
              setOpenList(isOpen ? null : field.key);
              setListSearch('');
            }}>
            {FieldIcon && <FieldIcon sx={{ fontSize: '18px', color: '#8c8c8c', flexShrink: 0 }} />}
            <ListSelectLabel placeholder={selected.length === 0 ? 1 : 0} centered={isProductField ? 1 : 0}>
              {boxLabel}
            </ListSelectLabel>
            <ListSelectCaret>▼</ListSelectCaret>
          </ListSelectBox>

          {isOpen && renderListPopover(field, options)}
        </ListSelectAnchor>

        {field.multi && selected.length > 0 && !isProductField && (
          <SelectedChipRow>
            {selected.map(value => (
              <SelectedChip key={value}>
                <span>{options.find(option => option.value === value)?.label ?? value}</span>
                <SelectedChipRemove onClick={() => handleToggle(field, value)}>×</SelectedChipRemove>
              </SelectedChip>
            ))}
          </SelectedChipRow>
        )}
      </>
    );
  };

  return (
    <FilterPanelCard>
      <FilterSearchRow>
        <FilterSearchBox>
          <SearchOutlinedIcon sx={{ fontSize: '19px', color: '#8c8c8c', flexShrink: 0 }} />
          <FilterSearchInput
            type="search"
            placeholder="회원 이름 · 전화번호 · 상품명"
            value={searchText}
            onChange={event => handleSearchText(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing && !loading) {
                onSearch();
              }
            }}
          />
          {searchText && (
            <FilterSearchClear aria-label="검색어 지우기" onClick={() => handleSearchText('')}>
              ×
            </FilterSearchClear>
          )}
        </FilterSearchBox>
        {/* 상품 선택 — 검색과 같은 '찾기' 도구라 검색창 바로 아래, 탭 밖에 둔다. */}
        <ProductPickRow>
          <ProductCategorySelect
            aria-label="상품 종류"
            value={effectiveProductCategory()}
            onChange={event => setProductCategory(event.target.value)}>
            {/* 옆 상품 드롭다운도 '전체'라 둘이 구분 안 됐다 — 선택값 앞에 '구분:'을 단다. */}
            <option value="">구분: 전체</option>
            {allowedProductSections().map(section => (
              <option key={section.value} value={section.value}>
                구분: {section.label}
              </option>
            ))}
          </ProductCategorySelect>
          {renderList(PRODUCT_FIELD)}
        </ProductPickRow>
      </FilterSearchRow>

      <FilterTabBar>
        <SegmentTrack>
          {FILTER_GROUPS.map(group => {
            const count = countActiveFields(
              filters,
              FILTER_FIELDS.filter(field => field.group === group.key),
            );
            const selected = group.key === activeGroup;
            return (
              <FilterTab
                key={group.key}
                selected={selected}
                onClick={() => {
                  setActiveGroup(group.key);
                  setOpenList(null);
                }}>
                <FilterTabLabel>{group.tabLabel}</FilterTabLabel>
                {count > 0 && <FilterTabBadge selected={selected}>{count}</FilterTabBadge>}
              </FilterTab>
            );
          })}
        </SegmentTrack>
      </FilterTabBar>

      <FilterFieldList>
        {currentFields.map(field => (
          <FilterFieldRow key={field.key}>
            <FilterFieldLabel>{field.label}</FilterFieldLabel>
            <FilterFieldControl>
              {field.type === 'chip' ? (
                renderChips(field)
              ) : (
                renderList(field)
              )}
            </FilterFieldControl>
          </FilterFieldRow>
        ))}
      </FilterFieldList>

      <FilterFooter>
        {hasSummary && (
          <FilterSummaryRow>
            <FilterStatusPill>{`조건 ${tokens.length}개`}</FilterStatusPill>
            {shownTokens.map(token => (
              <FilterTokenChip key={token.key}>
                <FilterTokenField>{token.label}</FilterTokenField>
                <FilterTokenValue>{token.value}</FilterTokenValue>
                <FilterTokenRemove onClick={() => onRemoveToken(token.key)}>×</FilterTokenRemove>
              </FilterTokenChip>
            ))}
            {moreCount > 0 && (
              <FilterMoreBtn onClick={() => setTokensOpen(true)}>
                <span>+{moreCount} 더보기</span>
                <span style={{ fontSize: '8px' }}>▼</span>
              </FilterMoreBtn>
            )}
            {tokensOpen && tokens.length > TOKEN_PREVIEW_COUNT && (
              <FilterMoreBtn collapse onClick={() => setTokensOpen(false)}>
                <span>접기</span>
                <span style={{ fontSize: '8px' }}>▲</span>
              </FilterMoreBtn>
            )}
          </FilterSummaryRow>
        )}

        <FilterActions>
          <SearchButton disabled={loading} onClick={() => !loading && onSearch()}>
            설정한 조건으로 매출검색
          </SearchButton>
          <ResetButton onClick={() => !loading && onReset()}>초기화</ResetButton>
        </FilterActions>
      </FilterFooter>
    </FilterPanelCard>
  );
};

export default SalesManagFilterPanel;
